import { useLingui } from '@lingui/react/macro';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { IconContainer, type IconContainerSize } from '@/components/blocks/icon-container';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

/**
 * Maps the IconContainer size enum to the Tailwind classes for the matching
 * title type ramp. Defined as a function rather than a record so it inlines
 * cleanly when callers pass a literal size.
 */
function getTitleSize(size: IconContainerSize): string {
  // WFM headings use the frecl H3 token: 26/36px, semibold, brand160.
  // The default ramp uses Tailwind sizes so non-WFM hosts keep Evo styling.
  const headingColour = 'text-[var(--heading-foreground)]';
  switch (size) {
    case 32:
      return `text-[length:var(--section-title-font-size)] leading-[var(--section-title-line-height)] font-semibold tracking-tight ${headingColour}`; // Small
    case 36:
      return `text-[length:var(--subheading-font-size)] leading-[var(--subheading-line-height)] font-semibold tracking-tight ${headingColour}`; // Small-medium
    case 44:
      return `text-[length:var(--heading-font-size)] leading-[var(--heading-line-height)] font-[var(--heading-font-weight)] tracking-tight ${headingColour}`; // Medium (default)
    case 52:
      return `text-[length:var(--heading-font-size)] leading-[var(--heading-line-height)] font-[var(--heading-font-weight)] tracking-tight ${headingColour}`; // Large
    case 72:
      return `text-[length:var(--heading-font-size)] leading-[var(--heading-line-height)] font-bold tracking-tight ${headingColour}`; // Extra large
    case 96:
      return `text-[length:var(--heading-font-size)] leading-[var(--heading-line-height)] font-bold tracking-tight ${headingColour}`; // Huge
    default:
      return `text-[length:var(--heading-font-size)] leading-[var(--heading-line-height)] font-[var(--heading-font-weight)] tracking-tight ${headingColour}`;
  }
}

export interface PageHeaderBlockProps extends Readonly<React.HTMLAttributes<HTMLElement>> {
  /** Main page title — required because a header without a title is meaningless. */
  readonly title: string;
  /** Optional subtitle below the title. */
  readonly subtitle?: string;
  /** Size of the header — affects icon container and title type ramp. */
  readonly size?: IconContainerSize;
  /** Right-aligned action slot — consumers inject their own primary actions. */
  readonly actions?: React.ReactNode;
  /** Whether to render the breadcrumb. Defaults true; empty `breadcrumbItems` hides it regardless. */
  readonly showBreadcrumb?: boolean;
  /** Breadcrumb trail. Empty (the default) hides the breadcrumb entirely. */
  readonly breadcrumbItems?: Array<{ label: string; href?: string; isCurrentPage?: boolean }>;
  /** Whether to use a product icon (transparent container) instead of a regular icon. */
  readonly useProductIcon?: boolean;
  /** Product icon element rendered when `useProductIcon` is true. */
  readonly productIcon?: React.ReactNode;
  /** Icon rendered inside the `IconContainer`. When omitted (and no `productIcon`), the icon container is not rendered at all. */
  readonly icon?: React.ReactNode;
}

/**
 * Page header block — title, optional subtitle, optional breadcrumb above,
 * optional icon container to the left, optional `actions` slot to the right.
 *
 * EDS-flavoured from `@eds/page-header` but patched so the component is
 * usable in production: no demo defaults leak (`title` is required;
 * `subtitle`, `breadcrumbItems`, `actions`, `icon` all default to nothing
 * visible), and the heading ID is generated with `useId()` so multiple
 * instances on a page don't collide (e.g. modal + main content).
 */
export function PageHeader({
  className,
  title,
  subtitle,
  size = 44,
  actions,
  showBreadcrumb = true,
  breadcrumbItems = [],
  useProductIcon = false,
  productIcon,
  icon,
  ...props
}: PageHeaderBlockProps) {
  const { t } = useLingui();
  const headingId = React.useId();
  const renderBreadcrumb = showBreadcrumb && breadcrumbItems.length > 0;
  const renderIconContainer = useProductIcon || icon !== undefined;

  return (
    <header
      data-slot="page-header-block"
      className={cn('space-y-4', className)}
      aria-labelledby={headingId}
      {...props}
    >
      {renderBreadcrumb && (
        <Breadcrumb aria-label={t`Breadcrumb`}>
          <BreadcrumbList>
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={`${item.label}-${index}`}>
                <BreadcrumbItem>
                  {item.isCurrentPage ? (
                    <BreadcrumbPage aria-current="page">{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink href={item.href ?? '#'}>{item.label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      )}
      <div className="flex flex-row items-center justify-between">
        <div className="flex gap-4 flex-row items-center">
          {renderIconContainer && (
            <IconContainer
              size={size}
              color="primary"
              useProductIcon={useProductIcon}
              productIcon={productIcon}
              aria-hidden={!!productIcon && useProductIcon ? true : undefined}
            >
              {icon}
            </IconContainer>
          )}
          <div>
            <h1 id={headingId} className={getTitleSize(size)}>
              {title}
            </h1>
            {subtitle && (
              <p className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2" aria-label={t`Header actions`}>
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

export default PageHeader;
